/**
 * @fileoverview The "Water today?" section (B152; owner, 2 Nov 2026: "I would like that to show permanently right under
 * the almanac bit of information. So the first thing, even above to-dos, is water today" and "i want it like water today?
 * and then the todo item there if needed. otherwise a text line stating no need"). It is always the first thing under
 * the Almanac, above the weather card and the to-do groups: the server's question as a heading (the group-heading
 * style, no count), and under it either the day's water job card(s), named by the server (water.jobKeys; a Yes day), or
 * the one-line answer (WaterCard: the reason there is no need).
 *
 * The server decides what is shown and where; this only places it. The job cards are JobCard's own (through MarkedJobCard), the ticks are
 * useTodoMarks' (the same marks as the to-do groups); the jobs are held out of the groups by the server, so each is drawn
 * once. Every word is the server's.
 *
 * Where: TodayPage, between the Almanac and the weather card.
 * API: none of its own.
 * Words: none of its own.
 *
 * @module WaterSection
 */

import React from 'react';
import type { BriefTodo, BriefWater, TodoJob } from '@/shared/api/todayBrief';
import { AnimatedList } from '@/shared/components/ui/AnimatedList';
import { MarkedJobCard, withOpen } from './MarkedJobCard';
import { WaterCard } from './WaterCard';
import { GROUP_HEADING } from './TodoGroups';
import type { TodoMarks } from '../lib/useTodoMarks';

/** What the section shows. */
export interface WaterSectionProps {
  /** The brief's water: its question, its answer and the keys of the water job(s) to draw. */
  water: BriefWater;
  /** The one to-do list, whose jobs the keys name. */
  todo: BriefTodo;
  /** Marking, from useTodoMarks. */
  marks: TodoMarks;
  /** The job an email's link asked for (its key); opened and scrolled to when it is one of the section's; null for none. */
  openKey?: string | null;
}

/**
 * The section: the question, then the water job cards the server named, else the one-line answer.
 *
 * @param props - WaterSectionProps.
 */
export const WaterSection: React.FC<WaterSectionProps> = ({ water, todo, marks, openKey = null }) => {
  const byKey = React.useMemo(() => new Map(todo.jobs.map((job) => [job.key, job])), [todo.jobs]);
  const jobs = water.jobKeys.map((key) => byKey.get(key)).filter((job): job is TodoJob => job !== undefined);
  /** The jobs whose plants are open, by key; an email's job opens first. */
  const [opened, setOpened] = React.useState<ReadonlySet<string>>(() => new Set(openKey ? [openKey] : []));
  const openRef = React.useRef<HTMLDivElement | null>(null);
  const scrolled = React.useRef(false);
  React.useEffect(() => {
    if (!openKey || scrolled.current || !openRef.current) return;
    scrolled.current = true;
    openRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  if (!water.answer) return null;
  return (
    <section aria-labelledby="todo-water">
      <h2 id="todo-water" className={GROUP_HEADING}>{water.question}</h2>
      {jobs.length > 0 ? (
        <AnimatedList
          className="space-y-2"
          items={jobs.map((job) => ({
            key: job.key,
            node: (
              <MarkedJobCard
                job={job}
                words={todo.words}
                marks={marks}
                open={opened.has(job.key)}
                onOpenChange={(open) => setOpened((current) => withOpen(current, job.key, open))}
                cardRef={job.key === openKey ? openRef : undefined}
              />
            ),
          }))}
        />
      ) : (
        <WaterCard water={water} />
      )}
    </section>
  );
};
